/**
 * bankMatching/matchOutgoing — Ausgangs-Matching (BR-5) + Vorstufen.
 *
 * Ordnet Belastungen (`amountCents < 0`) Ausgaben (`expenses`) zu und liefert
 * die beiden geld-kritischen Vorstufen:
 *  - `detectInternalTransfers` (§8.6 Vorstufe 1): beidseitige Umbuchungs-
 *    Erkennung, FAIL-CLOSED ohne IBAN-Evidenz.
 *  - `detectPaymentReversals` (§8.9): Rücklastschrift-Erkennung, FAIL-CLOSED
 *    (exakter Betrag + ≤60 Tage + Identitäts-Signal).
 *  - `selectTransactionsWithoutReceipt`: „Ausgaben ohne Beleg" (Kern-Deliverable),
 *    schließt erkannte Umbuchungen + Rücklastschriften aus.
 *
 * Pure, deterministisch, erklärbar (Score-Tabelle §8.6, kanonische Matrix §8.10).
 * Fremdwährungs-Txs werden defensiv gefiltert (Belegtest M9).
 *
 * @module bankMatching/matchOutgoing
 */

import { BankTransaction, BankAccount } from '../../types/bankTypes';
import {
  MatchableExpense,
  MatchSuggestion,
  MatchReason,
  InternalTransferPair,
  ReversalSuggestion,
  MATCH_REASON,
  MIN_SUGGESTION_SCORE,
  AUTO_MIN_SCORE,
  AUTO_UNIQUENESS_GAP,
} from './types';
import {
  normalizeRef,
  normalizeReturnRef,
  refContains,
  nameSimilarity,
  normalizeIban,
  addDaysToISO,
  isoDaysDiff,
} from './normalize';
import { sortSuggestions } from './matchIncoming';

/** Score-Konstanten (§8.6). */
const SCORE = {
  NUMBER_EXACT: 40,
  IBAN_EXACT: 35,
  AMOUNT_EXACT: 30,
  NAME_STRONG: 15,
  NAME_WEAK: 8,
  DATE_PLAUSIBLE: 5,
  DATE_BEFORE_DOC: -20,
} as const;

const NAME_STRONG_THRESHOLD = 0.8;
const NAME_WEAK_THRESHOLD = 0.5;

/** Maximaldifferenz (Tage) für die Umbuchungs-Spiegelsuche (§8.6 Vorstufe 1). */
const INTERNAL_TRANSFER_MAX_DAYS = 3;
/** Maximaldifferenz (Tage) für die Rücklastschrift-Zuordnung (§8.9). */
const REVERSAL_MAX_DAYS = 60;

/** Return-Indikatoren im Verwendungszweck (normalisiert) — §8.9. */
const REVERSAL_PURPOSE_TOKENS = [
  'RUECKLASTSCHRIFT',
  'RETOURE',
  'RETURN',
  'RUECKBUCHUNG',
];
/** CAMT-`BkTxCd`-Family-Kürzel, die eine Rückbuchung anzeigen — §8.9. */
const REVERSAL_TXCODE_TOKENS = ['RRTN', 'RTRN', 'RDDT'];

interface ScoredExpenseCandidate {
  expense: MatchableExpense;
  score: number;
  reasons: MatchReason[];
}

/** Bewertet ein einzelnes (tx, expense)-Paar gemäß §8.6. */
function scorePair(
  tx: BankTransaction,
  exp: MatchableExpense,
): ScoredExpenseCandidate {
  const reasons: MatchReason[] = [];
  let score = 0;

  // ── NUMBER_EXACT (Rechnungsnummer ≥5 normalisierte Zeichen im purpose) ──
  if (exp.invoiceNumber) {
    if (refContains(normalizeRef(tx.purpose), normalizeRef(exp.invoiceNumber))) {
      reasons.push({
        code: MATCH_REASON.NUMBER_EXACT,
        detail: `${exp.invoiceNumber} im Verwendungszweck`,
        scoreDelta: SCORE.NUMBER_EXACT,
      });
      score += SCORE.NUMBER_EXACT;
    }
  }

  // ── IBAN_EXACT ─────────────────────────────────────────────────────────
  const txIban = normalizeIban(tx.counterpartyIban);
  if (txIban != null && exp.supplierIban != null && txIban === exp.supplierIban) {
    reasons.push({
      code: MATCH_REASON.IBAN_EXACT,
      detail: 'Empfänger-IBAN entspricht Lieferant',
      scoreDelta: SCORE.IBAN_EXACT,
    });
    score += SCORE.IBAN_EXACT;
  }

  // ── AMOUNT_EXACT (abs, da Belastung negativ) ───────────────────────────
  if (Math.abs(tx.amountCents) === exp.grossCents) {
    reasons.push({
      code: MATCH_REASON.AMOUNT_EXACT,
      detail: 'Betrag == Bruttobetrag der Ausgabe',
      scoreDelta: SCORE.AMOUNT_EXACT,
    });
    score += SCORE.AMOUNT_EXACT;
  }

  // ── Namens-Signal (exklusiv) ───────────────────────────────────────────
  const sim = nameSimilarity(tx.counterpartyName, exp.supplierName);
  if (sim >= NAME_STRONG_THRESHOLD) {
    reasons.push({
      code: MATCH_REASON.NAME_STRONG,
      detail: 'Empfänger-Name entspricht Lieferant (stark)',
      scoreDelta: SCORE.NAME_STRONG,
    });
    score += SCORE.NAME_STRONG;
  } else if (sim >= NAME_WEAK_THRESHOLD) {
    reasons.push({
      code: MATCH_REASON.NAME_WEAK,
      detail: 'Empfänger-Name ähnelt Lieferant (schwach)',
      scoreDelta: SCORE.NAME_WEAK,
    });
    score += SCORE.NAME_WEAK;
  }

  // ── Datums-Signal (exklusiv; Karenz −5d, da Beleg oft nach Abbuchung datiert) ──
  const beforeCutoff = addDaysToISO(exp.expenseDateISO, -5);
  if (tx.bookingDate < beforeCutoff) {
    reasons.push({
      code: MATCH_REASON.DATE_BEFORE_DOC,
      detail: 'Buchung deutlich vor Belegdatum',
      scoreDelta: SCORE.DATE_BEFORE_DOC,
    });
    score += SCORE.DATE_BEFORE_DOC;
  } else {
    const upperBase = exp.dueDateISO ?? addDaysToISO(exp.expenseDateISO, 30);
    const upper = addDaysToISO(upperBase, 45);
    if (tx.bookingDate >= exp.expenseDateISO && tx.bookingDate <= upper) {
      reasons.push({
        code: MATCH_REASON.DATE_PLAUSIBLE,
        detail: 'Buchungsdatum im plausiblen Zahlungsfenster',
        scoreDelta: SCORE.DATE_PLAUSIBLE,
      });
      score += SCORE.DATE_PLAUSIBLE;
    }
  }

  return { expense: exp, score, reasons };
}

function hasNumberExact(c: ScoredExpenseCandidate): boolean {
  return c.reasons.some((r) => r.code === MATCH_REASON.NUMBER_EXACT);
}

function hasIbanAndAmountExact(c: ScoredExpenseCandidate): boolean {
  const codes = new Set(c.reasons.map((r) => r.code));
  return codes.has(MATCH_REASON.IBAN_EXACT) && codes.has(MATCH_REASON.AMOUNT_EXACT);
}

/**
 * Ausgangs-Matching (§8.6). Ordnet Belastungen Ausgaben zu.
 *
 * @param txs - Kontoumsätze (nur `amountCents < 0`, EUR; der Aufrufer schließt
 *   erkannte Umbuchungen/Rücklastschriften vorab aus, §8.6 Vorstufe 1/2).
 * @param expenses - Ausgaben-Kandidaten (Mapper; `alreadyMatched === false`
 *   und `grossCents > 0` werden hier erzwungen).
 * @param ownIbans - eigene IBANs (Signatur-Parität; für die Vorstufen genutzt,
 *   im Kern-Matching aktuell nicht scoring-relevant).
 * @param todayISO - heutiges Datum `YYYY-MM-DD`.
 * @returns Deterministisch sortiertes Array von `MatchSuggestion`.
 */
export function matchOutgoingTransactions(
  txs: BankTransaction[],
  expenses: MatchableExpense[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ownIbans: string[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  todayISO: string,
): MatchSuggestion[] {
  const candidates = expenses.filter(
    (e) => !e.alreadyMatched && e.grossCents > 0,
  );

  // Ausschluss NUR bei explizit gesetzter Fremdwährung (§8.6/§8.3);
  // fehlende/leere currency wird als inländisch (EUR) behandelt.
  const debitTxs = txs
    .filter((tx) => tx.amountCents < 0 && !(tx.currency && tx.currency !== 'EUR'))
    .slice()
    .sort((a, b) => {
      if (a.bookingDate !== b.bookingDate) {
        return a.bookingDate < b.bookingDate ? -1 : 1;
      }
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });

  const suggestions: MatchSuggestion[] = [];

  for (const tx of debitTxs) {
    const scored = candidates.map((exp) => scorePair(tx, exp));
    if (scored.length === 0) continue;

    const byScoreDesc = scored
      .slice()
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.expense.expenseId < b.expense.expenseId ? -1 : 1;
      });
    const best = byScoreDesc[0];
    const secondScore = byScoreDesc.length > 1 ? byScoreDesc[1].score : 0;

    let bestIsAuto = false;
    if (best.score >= AUTO_MIN_SCORE) {
      const gapOk = best.score - secondScore >= AUTO_UNIQUENESS_GAP;
      const signalOk = hasNumberExact(best) || hasIbanAndAmountExact(best);
      bestIsAuto = gapOk && signalOk;
    }

    for (const cand of scored) {
      if (cand.score < MIN_SUGGESTION_SCORE) continue;
      const isBest = cand === best;
      suggestions.push({
        txId: tx.id,
        kind: 'expense',
        targetIds: [cand.expense.expenseId],
        score: cand.score,
        confidence: isBest && bestIsAuto ? 'auto' : 'suggest',
        reasons: cand.reasons,
        skontoDetected: false,
      });
    }
  }

  return sortSuggestions(suggestions);
}

// ─────────────────────────────────────────────────────────────────────────
// Vorstufe 1 — interne Umbuchung (§8.6, beidseitig, FAIL-CLOSED)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Erkennt interne Umbuchungen zwischen eigenen Konten (§8.6 Vorstufe 1).
 *
 * Deterministische Regeln:
 *  1. IBAN-Evidenz: `counterpartyIban ∈ ownIbans` markiert einen Kandidaten.
 *  2. Spiegel-Suche: gegengleicher Betrag, ANDERES `accountId`, |Δ bookingDate|
 *     ≤ 3 Tage. Genau eine Gegenseite → Paar (BEIDE Seiten klassifiziert, auch
 *     wenn die Gegenseite selbst keine Gegen-IBAN trägt). Mehrere Kandidaten →
 *     kleinste Datums-Differenz, dann `txId ASC`.
 *  3. FAIL-CLOSED: ohne IBAN-Evidenz keine automatische Klassifikation.
 *
 * Eine IBAN-evidente Debit-Seite ohne Spiegel wird als Einzel-Klassifikation
 * (`creditTxId === null`) geliefert (§8.8, Gegenseite später importiert). Eine
 * lone IBAN-evidente CREDIT-Seite ohne Spiegel bleibt unklassifiziert, da die
 * `InternalTransferPair`-Signatur ein `debitTxId` erfordert — bewusste
 * v1-Grenze (der Nutzer markiert manuell).
 *
 * @param txs - ALLE geladenen Txs (beide Vorzeichen, ALLE Konten).
 * @param accounts - eigene Konten (liefern `ownIbans`).
 * @returns Umbuchungs-Paare, sortiert nach `debitTxId ASC`.
 */
export function detectInternalTransfers(
  txs: BankTransaction[],
  accounts: BankAccount[],
): InternalTransferPair[] {
  const ownIbans = new Set<string>();
  for (const acc of accounts) {
    const iban = normalizeIban(acc.iban);
    if (iban != null) ownIbans.add(iban);
  }
  if (ownIbans.size === 0) return []; // FAIL-CLOSED: keine IBAN-Evidenz möglich.

  const isIbanEvident = (tx: BankTransaction): boolean => {
    const iban = normalizeIban(tx.counterpartyIban);
    return iban != null && ownIbans.has(iban);
  };

  // Deterministische Iteration: Kandidaten nach txId ASC.
  const sortedTxs = txs
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const used = new Set<string>();
  const pairs: InternalTransferPair[] = [];

  for (const cand of sortedTxs) {
    if (used.has(cand.id)) continue;
    if (!isIbanEvident(cand)) continue;

    // Spiegel-Suche unter noch nicht verwendeten Txs.
    const mirrors = sortedTxs.filter(
      (other) =>
        other.id !== cand.id &&
        !used.has(other.id) &&
        other.accountId !== cand.accountId &&
        other.amountCents === -cand.amountCents &&
        Math.abs(isoDaysDiff(other.bookingDate, cand.bookingDate)) <=
          INTERNAL_TRANSFER_MAX_DAYS,
    );

    if (mirrors.length > 0) {
      // Kleinste Datums-Differenz, dann txId ASC.
      mirrors.sort((a, b) => {
        const da = Math.abs(isoDaysDiff(a.bookingDate, cand.bookingDate));
        const db = Math.abs(isoDaysDiff(b.bookingDate, cand.bookingDate));
        if (da !== db) return da - db;
        return a.id < b.id ? -1 : 1;
      });
      const mirror = mirrors[0];
      const debit = cand.amountCents < 0 ? cand : mirror;
      const credit = cand.amountCents < 0 ? mirror : cand;
      pairs.push({ debitTxId: debit.id, creditTxId: credit.id });
      used.add(cand.id);
      used.add(mirror.id);
      continue;
    }

    // Kein Spiegel: nur eine IBAN-evidente DEBIT-Seite wird einzeln klassifiziert.
    if (cand.amountCents < 0) {
      pairs.push({ debitTxId: cand.id, creditTxId: null });
      used.add(cand.id);
    }
    // Lone credit ohne Spiegel: unklassifiziert (siehe JSDoc).
  }

  return pairs.sort((a, b) => (a.debitTxId < b.debitTxId ? -1 : a.debitTxId > b.debitTxId ? 1 : 0));
}

// ─────────────────────────────────────────────────────────────────────────
// Vorstufe 2 — Rücklastschrift / Zahlungs-Storno (§8.9, FAIL-CLOSED)
// ─────────────────────────────────────────────────────────────────────────

/** Prüft, ob eine Debit-tx ein Return-/Rücklastschrift-Signal trägt (§8.9). */
function hasReturnIndicator(debit: BankTransaction): boolean {
  const code = (debit.bankTxCode ?? '').toUpperCase();
  if (REVERSAL_TXCODE_TOKENS.some((t) => code.includes(t))) return true;
  // Transliterierende Normalisierung (§8.9): deutsche Nativ-Schreibweisen
  // ("Rücklastschrift"/"Rückbuchung") müssen die spec-geforderten ASCII-Token
  // (RUECKLASTSCHRIFT/RUECKBUCHUNG) treffen. `normalizeRef` entfernt Umlaute
  // ersatzlos und würde hier fehlschlagen — daher `normalizeReturnRef`.
  const purposeNorm = normalizeReturnRef(debit.purpose);
  return REVERSAL_PURPOSE_TOKENS.some((t) => purposeNorm.includes(t));
}

/**
 * Erkennt Rücklastschriften (Rücknahme einer bereits verbuchten Gutschrift,
 * §8.9). FAIL-CLOSED — ALLE Kriterien müssen gelten:
 *  1. `abs(debit.amountCents) === credit.amountCents` (exakter Betrag),
 *  2. `debit.bookingDate ≥ credit.bookingDate` und Differenz ≤ 60 Tage,
 *  3. ≥1 Identitäts-Signal: gleiche `counterpartyIban`, ODER gleiche
 *     `endToEndId`, ODER Return-Indikator (`bankTxCode`/Verwendungszweck).
 *
 * Mehrdeutigkeit (mehrere Original-Gutschriften) → jüngste Gutschrift zuerst
 * (dann `txId ASC`); der Rest bleibt unvorgeschlagen. Jede Debit-tx liefert
 * höchstens EINEN Vorschlag; einmal zugeordnete Gutschriften werden nicht erneut
 * verwendet.
 *
 * @param unmatchedDebitTxs - Rücklastschrift-Kandidaten (`amountCents < 0`, unmatched).
 * @param matchedCreditTxs - gematchte Gutschriften (`matchStatus 'matched'`, `paymentIds ≥ 1`).
 * @returns Reversal-Vorschläge, sortiert nach `debitTxId ASC`.
 */
export function detectPaymentReversals(
  unmatchedDebitTxs: BankTransaction[],
  matchedCreditTxs: BankTransaction[],
): ReversalSuggestion[] {
  const debits = unmatchedDebitTxs
    .filter((tx) => tx.amountCents < 0)
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const credits = matchedCreditTxs.filter(
    (tx) => tx.amountCents > 0 && tx.paymentIds.length >= 1,
  );

  const usedCredits = new Set<string>();
  const results: ReversalSuggestion[] = [];

  for (const debit of debits) {
    const debitIban = normalizeIban(debit.counterpartyIban);
    const returnIndicator = hasReturnIndicator(debit);

    const matching = credits.filter((credit) => {
      if (usedCredits.has(credit.id)) return false;
      // 1. exakter Betrag.
      if (Math.abs(debit.amountCents) !== credit.amountCents) return false;
      // 2. Datumsfenster: debit ≥ credit und ≤ 60 Tage.
      const diff = isoDaysDiff(debit.bookingDate, credit.bookingDate);
      if (diff < 0 || diff > REVERSAL_MAX_DAYS) return false;
      // 3. ≥1 Identitäts-Signal.
      const creditIban = normalizeIban(credit.counterpartyIban);
      const ibanMatch = debitIban != null && creditIban != null && debitIban === creditIban;
      const e2eMatch =
        debit.endToEndId != null &&
        credit.endToEndId != null &&
        debit.endToEndId === credit.endToEndId;
      if (!ibanMatch && !e2eMatch && !returnIndicator) return false;
      return true;
    });

    if (matching.length === 0) continue;

    // Mehrdeutigkeit: jüngste Gutschrift zuerst (bookingDate DESC, dann txId ASC).
    matching.sort((a, b) => {
      if (a.bookingDate !== b.bookingDate) return a.bookingDate < b.bookingDate ? 1 : -1;
      return a.id < b.id ? -1 : 1;
    });
    const original = matching[0];
    usedCredits.add(original.id);
    results.push({
      debitTxId: debit.id,
      originalTxId: original.id,
      reversalOfPaymentIds: original.paymentIds.slice(),
    });
  }

  return results.sort((a, b) => (a.debitTxId < b.debitTxId ? -1 : a.debitTxId > b.debitTxId ? 1 : 0));
}

// ─────────────────────────────────────────────────────────────────────────
// „Ausgaben ohne Beleg" — pure Selektor (§8.6)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Wählt die Belastungen ohne Beleg aus (Kern-Deliverable, §8.6). Schließt
 * erkannte interne Umbuchungen (Vorstufe 1) und Rücklastschriften (§8.9) über
 * `excludedTxIds` aus, damit die Liste nur ECHTE Beleg-Lücken zeigt.
 *
 * @param txs - Kontoumsätze (typischerweise bereits `direction=='debit'` &
 *   `matchStatus=='unmatched'` aus der Index-Query; hier defensiv nachgefiltert).
 * @param excludedTxIds - IDs erkannter Umbuchungen + Rücklastschriften.
 * @returns Unmatched Debit-Txs ohne Ausschluss, sortiert nach bookingDate DESC
 *   (dann txId ASC), deterministisch.
 */
export function selectTransactionsWithoutReceipt(
  txs: BankTransaction[],
  excludedTxIds: Set<string>,
): BankTransaction[] {
  return txs
    .filter(
      (tx) =>
        tx.amountCents < 0 &&
        tx.matchStatus === 'unmatched' &&
        !excludedTxIds.has(tx.id),
    )
    .slice()
    .sort((a, b) => {
      if (a.bookingDate !== b.bookingDate) {
        return a.bookingDate < b.bookingDate ? 1 : -1; // DESC
      }
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
}
